import { useEffect, useState } from 'react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const QualityExportPage = () => {
  // Stocke l'index du certificat actuellement ouvert (null si fermé)
  const [activeCertIndex, setActiveCertIndex] = useState(null);

  const certifications = [
    { 
      title: "ISO 9001:2015", 
      desc: "Quality Management System",
      image: "./images/cert-iso-9001.jpg"
    },
    { 
      title: "ISO 22000:2018", 
      desc: "Food Safety Management (HACCP)",
      image: "./images/cert-iso-22000.jpg"
    },
    { 
      title: "ISO 14001:2015", 
      desc: "Environmental Management",
      image: "./images/cert-iso-14001.jpg"
    },
    { 
      title: "ISO 45001:2018", 
      desc: "Occupational Health & Safety",
      image: "./images/cert-iso-45001.jpg"
    }
  ];

  const countries = [
    "France", "England", "Canada", "Brazil", "South Korea", 
    "China", "Saudi Arabia", "Oman", "Kuwait", "Qatar", "Bahrain", "Mauritius"
  ];

  // Fonctions de navigation
  const handlePrev = () => {
    setActiveCertIndex((prevIndex) => 
      prevIndex === 0 ? certifications.length - 1 : prevIndex - 1
    );
  };

  const handleNext = () => {
    setActiveCertIndex((prevIndex) => 
      prevIndex === certifications.length - 1 ? 0 : prevIndex + 1
    );
  };

  // Gestion des touches du clavier (Échap, Flèche Gauche, Flèche Droite)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (activeCertIndex === null) return;
      if (e.key === 'Escape') setActiveCertIndex(null);
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'ArrowRight') handleNext();
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeCertIndex]);

  const activeCert = activeCertIndex !== null ? certifications[activeCertIndex] : null;

  return (
    <div className="min-h-screen bg-[#1A1915]">
      <Navigation />

      {/* Hero Section */}
      <section className="relative h-[70vh] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url(./images/quality-header.webp)',
            filter: 'brightness(0.4)'
          }}
        />
        <div className="relative z-10 text-center px-6">
          <h1 className="playfair text-5xl lg:text-7xl font-bold text-white mb-6">
            Quality Assurance & Export
          </h1>
          <div className="w-32 h-1 bg-[#C9A961] mx-auto"></div>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="py-24 px-6 bg-[#221F1A] stone-texture">
        <div className="max-w-6xl mx-auto">
          <h2 className="playfair text-4xl lg:text-5xl font-bold text-[#C9A961] text-center mb-16">
            Our Commitment to Excellence
          </h2>

          <div className="space-y-20">
            {/* Process Section */}
            <div className="grid md:grid-cols-2 gap-12 items-center">
              <div>
                <h3 className="playfair text-3xl font-semibold text-[#C9A961] mb-6">
                  A Meticulous Journey from Grove to Bottle
                </h3>
                <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                  <p>
                    Originating from the generations-rich Haron family estate established in 1971, 
                    we continuously merge traditional hand-harvesting patience with clean, modern manufacturing.
                  </p>
                  <p>
                    <span className="text-[#C9A961] font-semibold">Strict Selection:</span> Our olives are rigorously monitored 
                    on the trees and hand-picked at exact peak maturity to ensure minimal natural degradation and superb flavor density.
                  </p>
                  <p>
                    <span className="text-[#C9A961] font-semibold">Controlled Storage:</span> All bulk oil is stored immediately 
                    in climate-controlled stainless steel containers to prevent exposure to warmth and protect vital nutritional compounds.
                  </p>
                </div>
              </div>
              <div className="relative">
                <img
                  src="./images/excellence.webp"
                  alt="Modern bottling facility quality control"
                  className="rounded-lg shadow-2xl"
                />
              </div>
            </div>

            {/* Certifications Box */}
            <div className="bg-[#2A2720] rounded-lg p-12 border-2 border-[#C9A961]/30">
              <h3 className="playfair text-3xl font-semibold text-[#C9A961] text-center mb-4">
                Global Standards & Certifications
              </h3>
              <p className="text-gray-400 text-center italic max-w-2xl mx-auto mb-10">
                Our management systems and bottling lines function in strict alignment with rigid international food safety parameters. Click on any certificate to open the gallery.
              </p>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
                {certifications.map((cert, index) => (
                  <button
                    key={index}
                    onClick={() => setActiveCertIndex(index)}
                    className="group border border-[#C9A961]/20 bg-[#1A1915] p-6 text-center rounded-lg hover:border-[#C9A961] hover:bg-[#2A2720] transition-all duration-300 flex flex-col items-center justify-center cursor-pointer focus:outline-none"
                  >
                    <div className="w-10 h-10 rounded-full bg-[#C9A961]/10 flex items-center justify-center mb-3 group-hover:bg-[#C9A961]/20 transition-colors">
                      <svg className="w-5 h-5 text-[#C9A961]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <h4 className="text-white font-bold tracking-wide mb-1 group-hover:text-[#C9A961] transition-colors">{cert.title}</h4>
                    <p className="text-xs text-gray-400">{cert.desc}</p>
                    <span className="text-[10px] text-[#C9A961]/50 mt-3 underline opacity-0 group-hover:opacity-100 transition-opacity">View Document</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Global Logistics Section */}
            <div className="text-center">
              <h3 className="playfair text-3xl font-semibold text-[#C9A961] mb-6">
                The Tunisian Advantage & Global Distribution
              </h3>
              <div className="max-w-3xl mx-auto space-y-4 text-gray-300 text-lg leading-relaxed mb-12">
                <p>
                  Tunisia stands proud as the <span className="text-[#C9A961] font-semibold">#2 global exporter</span> of premium olive oil, 
                  supplying the most demanding culinary markets across Europe, Asia, and the Americas.
                </p>
                <p>
                  With an agile commercial setup completely designed around complex overseas logistics, our bottling lines 
                  maintain an active export capacity of <span className="text-[#C9A961] font-semibold">two containers per day</span>.
                </p>
              </div>

              <div className="max-w-4xl mx-auto mb-12 bg-[#2A2720] border border-[#C9A961]/20 p-4 rounded-lg shadow-xl">
                <img
                  src="./images/export-map.jpg"
                  alt="Legato d'Oro Global Export Destinations"
                  className="w-full rounded-lg h-auto object-cover"
                />
              </div>

              <h4 className="text-white font-medium tracking-wide mb-6 uppercase text-sm">Active International Markets</h4>
              <div className="flex flex-wrap justify-center gap-3 max-w-4xl mx-auto">
                {countries.map((country, index) => (
                  <div key={index} className="bg-[#2A2720] border border-[#C9A961]/20 text-gray-300 px-4 py-2 rounded-full text-sm font-medium tracking-wide shadow-md">
                    📍 {country}
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* COMPOSANT MODAL AVEC NAVIGATION INTERNE */}
      {activeCert && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/85 backdrop-blur-sm"
          onClick={() => setActiveCertIndex(null)}
        >
          {/* Bouton Flèche Gauche */}
          <button 
            onClick={(e) => { e.stopPropagation(); handlePrev(); }}
            className="absolute left-4 md:left-8 z-55 text-gray-400 hover:text-[#C9A961] p-3 rounded-full bg-[#1A1915]/80 border border-[#C9A961]/20 hover:border-[#C9A961] transition-all cursor-pointer focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          {/* Corps de la Modal */}
          <div 
            className="relative max-w-3xl w-full bg-[#2A2720] border border-[#C9A961] rounded-lg p-6 shadow-2xl max-h-[90vh] flex flex-col mx-12"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header de la Modal */}
            <div className="flex items-center justify-between border-b border-[#C9A961]/20 pb-4 mb-4">
              <div>
                <h3 className="playfair text-xl font-bold text-[#C9A961]">{activeCert.title}</h3>
                <p className="text-xs text-gray-400">{activeCert.desc}</p>
              </div>
              <div className="flex items-center space-x-4">
                <span className="text-xs text-gray-500 font-mono">{activeCertIndex + 1} / {certifications.length}</span>
                <button 
                  onClick={() => setActiveCertIndex(null)}
                  className="text-gray-400 hover:text-white p-1.5 rounded-full bg-[#1A1915] border border-[#C9A961]/20 hover:border-[#C9A961] transition-all cursor-pointer focus:outline-none"
                >
                  ✕
                </button>
              </div>
            </div>
            
            {/* Affichage de l'image */}
            <div className="overflow-y-auto flex-1 flex items-center justify-center bg-[#1A1915] rounded border border-[#C9A961]/10 p-2 relative group">
              <img 
                src={activeCert.image} 
                alt={activeCert.title}
                className="max-h-[62vh] w-auto object-contain rounded transition-all duration-300"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://via.placeholder.com/600x800?text=Certificate+Image+Coming+Soon";
                }}
              />
            </div>
          </div>

          {/* Bouton Flèche Droite */}
          <button 
            onClick={(e) => { e.stopPropagation(); handleNext(); }}
            className="absolute right-4 md:right-8 z-55 text-gray-400 hover:text-[#C9A961] p-3 rounded-full bg-[#1A1915]/80 border border-[#C9A961]/20 hover:border-[#C9A961] transition-all cursor-pointer focus:outline-none"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default QualityExportPage;